// fleet-generated: do not edit by hand
//
// Copied verbatim  : infra/fleet/artifacts/typography/font-preference.ts
// Parameters       : none — this file is byte-identical across the fleet
// Destination      : packages/fleet/typography/src/font-preference.ts
//
// TO CHANGE THIS FILE, edit the canonical copy in infra and re-render. It
// changes for the whole fleet at once. Editing it here is reverted by the
// next render, and the guard in this repo goes red in the meantime:
//
//     cd ../infra && node fleet/fleet.ts render --repo bodega-sync-app
//     node fleet/fleet.ts check --repo bodega-sync-app
//
// The generator runs LOCALLY and is never invoked by CI. CI only verifies
// that what is committed matches the hash in .github/fleet-lock.json, which
// also records WHICH infra commit produced it. The commit is deliberately
// not repeated here: one value, one place — and stamping it into every file
// meant a one-line template edit rewrote four files in 28 repos.
/**
 * ── Font preference — per-account, local-first resolution (agnostic) ──────────
 *
 * Pure logic (no DOM, no framework) for resolving the EFFECTIVE font choice of
 * the active account, mirroring the per-account language pattern (#18):
 *
 *  - The BRAND default is the absence of an override (`fontId === null`). It is
 *    ALWAYS available (bundled) and never depends on the catalog. Resetting =
 *    choosing `null`.
 *  - A real account's source of truth is its synced `user.font_family`; the
 *    per-account localStorage cache is only an instant-boot / offline fallback.
 *  - The GUEST has no synced preferences and IGNORES them entirely (during a
 *    real→guest switch the synced value can transiently still hold the previous
 *    account's font — trusting it would leak across accounts). The guest resolves
 *    from its OWN cache only.
 *  - The cache key is scoped by userId, so a font NEVER leaks across accounts.
 *  - A locally-picked value is held "pending" until the account's synced value
 *    catches up, so the async DB round-trip can't revert the fresh choice.
 *
 * This module is fully unit-tested and copy-paste agnostic across apps.
 */

export const FONT_CACHE_PREFIX = "@app_font:";

/** Per-account localStorage key for the cached font choice (instant boot, no leak). */
export function fontCacheKey(userId: string): string {
  return `${FONT_CACHE_PREFIX}${userId}`;
}

/** A cached font choice: the catalog id + its display family name (to apply instantly). */
export interface CachedFont {
  id: string;
  name: string;
}

/** Minimal storage surface (a `Storage` or a test double). */
export type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem">;

function isCachedFont(value: unknown): value is CachedFont {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    "id" in value &&
    "name" in value &&
    typeof value.id === "string" &&
    typeof value.name === "string"
  );
}

/** Reads the per-account cached font choice; `null` = brand default (or unreadable). */
export function readCachedFont(
  storage: StorageLike | null | undefined,
  userId: string,
): CachedFont | null {
  if (!storage) {
    return null;
  }
  try {
    const raw = storage.getItem(fontCacheKey(userId));
    if (!raw) {
      return null;
    }
    const parsed: unknown = JSON.parse(raw);
    return isCachedFont(parsed) ? { id: parsed.id, name: parsed.name } : null;
  } catch {
    return null;
  }
}

/**
 * Writes the per-account cached font choice. `null` clears it (brand default),
 * which is exactly what "Reset to default" persists.
 */
export function writeCachedFont(
  storage: StorageLike | null | undefined,
  userId: string,
  font: CachedFont | null,
): void {
  if (!storage) {
    return;
  }
  try {
    if (font) {
      storage.setItem(fontCacheKey(userId), JSON.stringify({ id: font.id, name: font.name }));
    } else {
      storage.removeItem(fontCacheKey(userId));
    }
  } catch {
    // storage unavailable (private mode / quota) — ignore; the DB stays source of truth.
  }
}

export interface ResolveAvailabilityInput {
  /** The desired font id for the active account (`null` = brand default). */
  desiredFontId: string | null;
  /** Whether the desired family exists AND is active in the (synced) catalog. */
  familyFound: boolean;
  /**
   * Whether the catalog is present locally and settled — i.e. we can trust that
   * "the family is absent" means REMOVED, not merely "not synced yet". Compute it
   * as `catalog query settled && at least one other font is visible` so an empty/
   * not-yet-synced catalog never false-positives a valid choice into "unavailable".
   */
  catalogLoaded: boolean;
}

export interface ResolveAvailabilityResult {
  /** Effective font id to APPLY: the desired one, or `null` (brand) when unavailable. */
  fontId: string | null;
  /**
   * True when the account HAS a desired font but it is no longer available in the
   * catalog (removed / deactivated / unapproved). The UI surfaces this and shows
   * the app's brand default as the selected fallback.
   */
  unavailable: boolean;
}

/**
 * Resolves whether the account's desired font is still available, falling back to
 * the BRAND default (`null`) when it is not. This is what makes a font that gets
 * removed/deactivated from the catalog degrade gracefully to the bundled brand
 * font instead of a broken generic face — and lets the UI both surface the loss
 * and show the default as the selected option.
 *
 * The `catalogLoaded` gate is essential: before the catalog has synced we CANNOT
 * distinguish "removed" from "not loaded yet", so we keep the desired choice (the
 * instant-boot cache still applies its name). We only declare a font unavailable
 * once we can see the catalog but not that font.
 */
export function resolveAvailableFontId(input: ResolveAvailabilityInput): ResolveAvailabilityResult {
  const { desiredFontId, familyFound, catalogLoaded } = input;

  // Brand default is always available (bundled, catalog-independent).
  if (desiredFontId === null) {
    return { fontId: null, unavailable: false };
  }
  // Can't yet prove it's gone — trust the choice (instant-boot / pre-sync).
  if (!catalogLoaded) {
    return { fontId: desiredFontId, unavailable: false };
  }
  // Catalog is present: the font is authoritative iff its family is there.
  if (familyFound) {
    return { fontId: desiredFontId, unavailable: false };
  }
  // Catalog present but this font is gone → fall back to brand, surface to UI.
  return { fontId: null, unavailable: true };
}

export interface ResolveFontInput {
  /** Active account of this tab. */
  isGuest: boolean;
  /** Whether the synced preferences query has settled (false = still loading). */
  preferencesLoaded: boolean;
  /** Synced `user.font_family` for a real account (`null` = brand). Ignored for guests. */
  remoteFontId: string | null;
  /** Per-account cached font id (`null` = none/brand). */
  cachedFontId: string | null;
  /** In-flight local choice for this account; `undefined` = none pending. */
  pendingFontId: string | null | undefined;
}

export interface ResolveFontResult {
  /** Effective chosen font id; `null` = BRAND default (bundled, no override). */
  fontId: string | null;
  /** True when the synced value caught up to the pending choice → caller clears pending. */
  clearPending: boolean;
}

/**
 * Resolves the effective font id for the active account. `null` means the app's
 * bundled BRAND default (no runtime override) — this is what Reset produces and
 * what a fresh account / empty catalog resolves to.
 */
export function resolveDesiredFontId(input: ResolveFontInput): ResolveFontResult {
  const { isGuest, preferencesLoaded, remoteFontId, cachedFontId, pendingFontId } = input;

  // A pending local choice wins until the account's synced value confirms it.
  if (pendingFontId !== undefined) {
    // Guests never sync, so their pending choice is simply the resolved value.
    const caughtUp = !isGuest && preferencesLoaded && remoteFontId === pendingFontId;
    return { fontId: pendingFontId, clearPending: caughtUp };
  }

  // The guest ignores synced preferences entirely (anti-leak): cache-only.
  if (isGuest) {
    return { fontId: cachedFontId, clearPending: false };
  }

  // Real account: the synced value is authoritative ONCE it has loaded (even a
  // `null` there means the user explicitly reset to brand). While still loading,
  // fall back to the per-account cache for an instant boot.
  if (preferencesLoaded) {
    return { fontId: remoteFontId, clearPending: false };
  }
  return { fontId: cachedFontId, clearPending: false };
}
